import { useEffect, useState } from "react";

import { useHandleCalendar } from "0.Mine/1.Hooks/business/CalendarEvent";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { Event } from "0.Mine/3.Interfaces/Event";

export const useBusinessCalendarLogic = () => {
  const { createCalendarEvent, editCalendarEvent, deleteCalendarEvent, getAllCalendarEvents } =
    useHandleCalendar();
  const { session } = useBusinessContext();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (session && session.selectedBusiness) {
        const data = await getAllCalendarEvents(session.selectedBusiness.id);
        if (data) {
          setEvents(data);
        }
      }
    };
    fetchData();
  }, [session]);

  const handleSelectEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    setSelectedEvent({
      id: "",
      title: "",
      start,
      end,
      description: "",
      location: "",
      remindOnWhatsapp: true,
      remindViaEmail: true,
      sharedWith: [],
      userId: "",
      vendorId: [],
    });
    setIsFormOpen(true);
    setIsEditMode(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  const handleEditEvent = () => {
    setIsModalOpen(false);
    setIsFormOpen(true);
    setIsEditMode(true);
  };

  const handleDeleteEvent = async (id: string) => {
    setEvents(events.filter((event) => event.id !== id));
    setIsModalOpen(false);
    await deleteCalendarEvent(Number(id), session.selectedBusiness!.id);
  };

  const handleSaveEvent = async (event: Event) => {
    if (isEditMode && selectedEvent) {
      try {
        const eventId = Number(selectedEvent.id);
        if (isNaN(eventId)) {
          throw new Error("Invalid event ID from backend");
        }

        const updatedEvent = {
          ...event,
          id: selectedEvent.id,
        };

        await editCalendarEvent(eventId, updatedEvent, session.selectedBusiness!.id);

        const data = await getAllCalendarEvents(session.selectedBusiness!.id);
        if (data) {
          setEvents(data);
        }
      } catch (error) {
        const data = await getAllCalendarEvents(session.selectedBusiness!.id);
        if (data) {
          setEvents(data);
        }
      }
    } else {
      const newEvent = {
        ...event,
        id: "",
      };
      await createCalendarEvent(newEvent, session.selectedBusiness!.id);

      const data = await getAllCalendarEvents(session.selectedBusiness!.id);
      if (data) {
        setEvents(data);
      }
    }
    setIsFormOpen(false);
  };

  return {
    session,
    events,
    selectedEvent,
    isModalOpen,
    isFormOpen,
    isEditMode,
    handleSelectEvent,
    handleSelectSlot,
    handleCloseModal,
    handleCloseForm,
    handleEditEvent,
    handleDeleteEvent,
    handleSaveEvent,
  };
};
